import React from 'react';
import { useSelector } from 'react-redux';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ChartContent from '../base/ChartContent.jsx';
import { useBaseWidget } from '../../hooks/useBaseWidget';
import { useDataset } from '../../hooks/useDataset.js';
import { useChartWidget } from '../../hooks/useChartWidget.js';

const VALUE_LABEL = ["single", "clustered", "clustered_aggregated"];

function BarChartDatasetWidget({ widgetId, widgetRef }) {
    const { formatNumber, numberWithUnit } = useBaseWidget();
    const { getNumberUnit, getNumberPrecision, getCommonNumberUnit, data } = useDataset({ widgetId });
    const { legendTitleFormatter, tooltipTextFormatter } = useChartWidget();
    const display_labels_on_data = useSelector(state => state.dashboard.widgets[widgetId].settings?.display_labels_on_data);
    const legend_text_color = useSelector(state => state.dashboard.widgets[widgetId].settings?.legend_text_color);
    const number_format = useSelector(state => state.dashboard.widgets[widgetId].settings?.number_format);
    const columns = useSelector(state => state.dashboard.widgets[widgetId].settings?.columns) || "single";
    const show_grid = useSelector(state => state.dashboard.widgets[widgetId].settings?.show_grid);
    const grid_stroke_width = useSelector(state => state.dashboard.widgets[widgetId].settings?.grid_stroke_width);
    const grid_line_color = useSelector(state => state.dashboard.widgets[widgetId].settings?.grid_line_color);
    const datasets = useSelector(state => state.dashboard.widgets[widgetId].settings?.datasets) || [];
    const xaxis_labels = useSelector(state => state.dashboard.widgets[widgetId].settings?.xaxis_labels) || "";
    const colorScheme = useSelector(state => state.dashboard.colorScheme);
    const getChartPlugins = () => {
        let with_labels = display_labels_on_data == "with_labels";
        return with_labels ? [ChartDataLabels] : [];
    }

    const getChartOptions = ({ chartData }) => {
        return {
        maintainAspectRatio: false,
        devicePixelRatio: 2,
        plugins: {
            datalabels: {
                anchor: "end",
                align: "top",
                offset: 2,
                labels: {
                    value: {
                        color: legend_text_color
                    }
                },
                formatter: (value, context) => {
                    if (!VALUE_LABEL.includes(columns)) return '';
                    return formatNumber(value, getNumberUnit(context.dataIndex), getNumberPrecision(context.dataIndex), number_format);
                }
            },
            tooltip: {
                callbacks: {
                    title: (tips) => {
                        const finalTitle = tips[0].dataset.label != "-" ? tips[0].dataset.label : tips[0].label;
                        return tooltipTextFormatter(finalTitle);
                    },
                    label: (tooltipItem) => {
                        const datasetIndex = columns != "single" ? tooltipItem.datasetIndex : tooltipItem.dataIndex;
                        return formatNumber(tooltipItem.parsed?.y, getNumberUnit(datasetIndex), getNumberPrecision(datasetIndex), number_format);
                    },
                },
            },
            legend: {
                display: xaxis_labels != "on_axis",
                onClick: (e, legendItem, legend) => {
                    var chart = legend.chart;
                    if (Array.isArray(chart.data.datasets) && chart.data.datasets.length > 1) {
                        let index = legendItem.datasetIndex;
                        let meta = chart.getDatasetMeta(index);
                        meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;
                    } else {
                        chart.toggleDataVisibility(legendItem.dataIndex);
                    }
                        chart.update();
                },
                labels: {
                    color: legend_text_color,
                    padding: 7,
                    font: {
                    size: 15
                    },
                    generateLabels: (chart) => {
                        let data = chart.data;
                        const title_refactoring = (title, i) => {
                            // Always display legend on aggregated
                            if (columns !== 'single'){
                            return legendTitleFormatter(title, widgetId);
                            } 

                            let text = "";
                            switch(xaxis_labels) {
                            case "on_legend":
                                text = title; break;
                            case "numbered_legend":
                                text = `${i + 1} - ${title}`; break;
                            }
                            return legendTitleFormatter(text, widgetId);
                        }

                        if (Array.isArray(data.datasets) && data.datasets.length > 1)
                            return data.datasets.map(function(dataset, i) {
                            return {
                                text: title_refactoring(dataset.label, i),
                                fillStyle: (!Array.isArray(dataset.backgroundColor) ? dataset.backgroundColor : dataset.backgroundColor[0]),
                                hidden: !chart.isDatasetVisible(i),
                                strokeStyle: dataset.borderColor,
                                pointStyle: dataset.pointStyle,
                                datasetIndex: i
                                };
                            }, this)
                        else if(Array.isArray(data.labels) && data.datasets.length > 0) {
                            let visibility = [];
                            for(let i = 0; i < data.labels.length; i++) {
                            visibility.push(chart.getDataVisibility(i) === true ? false : true)
                            }
                            return data.labels.map(function(label, i) {
                            return {
                                text: title_refactoring(label, i),
                                strokeStyle: (data.datasets[0].borderColor || {})[i],
                                fillStyle: !Array.isArray(data.datasets[0].backgroundColor) ? data.datasets[0].backgroundColor : (data.datasets[0].backgroundColor || {})[i],
                                hidden: visibility[i],
                                datasetIndex: i
                            };
                            })
                        } else [];
                    }
                }
            },
        },
        scales: {
            x: {
                stacked: !!columns.match("stacked"),
                ticks: {
                    color: legend_text_color,
                    autoSkip: false,
                    // Why is 'label' the same as 'index' and not the actual label :/
                    callback: (label, i, labels) => {
                    // Always on aggergate, display the name of the dataset on axis. 
                    if (columns !== 'single') return chartData?.labels[i]?.toString().split(" ");

                    let text = "";
                    switch(xaxis_labels) {
                        case "on_axis":
                            text = chartData?.labels[i]?.toString().split(" "); break;
                        case "numbered_legend":
                        if(columns == "clustered" || columns == "stacked")
                            text = chartData?.labels[i]?.toString().split(" ");
                        else
                            text = i + 1;
                        break;
                    }
                    return text;
                    },
                },
                grid:{
                    display: show_grid == "true",
                    lineWidth: grid_stroke_width, 
                    color: grid_line_color, 
                }
            },
            y: {
                stacked: !!columns.match("stacked"),
                beginAtZero: true,
                suggestedMax: 6,
                grace: '1%',
                ticks: {
                    color: legend_text_color,
                    callback: (label, index, labels) => formatNumber(label, getCommonNumberUnit(), false, number_format),
                },
                grid:{
                    display: show_grid == "true",
                    lineWidth: grid_stroke_width,
                    color: grid_line_color, 
                }
            },
        },};
    }

    const formatData = () => {
        let labels = [];
        let values = [];
        let colors = [];
        let _datasets = [];
        let disaggregations = [];
        let disaggregatedData = {};

        let notConfigured = datasets.length == 0;

        // Aggregate different datasets to stack / cluster based on dataset labels.
        const aggregate = columns.match("aggregated");

        // Datasets does not always have same labels, get a list of unique labels and its color
        const colorSet = {}
        if(data){
        const labelList = []
        Object.values(data).forEach((d)=>{
            d.map((l)=>{
            if(!labelList.includes(l.label)){
                labelList.push(l.label)
            }
            })
        })
        labelList.sort((a, b) => a && b && a.toString().localeCompare(b.toString())).forEach((d,i)=>{
            colorSet[d] = colorScheme[i % colorScheme.length]
        })
        }

        datasets.forEach((d, i) => {
        // If any of the variables is missing dataset_id, report is not configured.
        notConfigured = notConfigured || !(d.dataset_id || d.value);
        if (columns == "single") {
            const singledData = ((data || {})[d.dataset_id] || [])
            // Use dataset label & value if defined for first item, otherwise fallback to static
            labels.push(d.name_item && d.name_item != '' ? d.name_item : singledData[0]?.label);
            colors.push(d.background_color);
            values.push(numberWithUnit(singledData[0]?.data ? singledData[0].data : d.value, d.number_unit));
            // Add the rest of the items from the dataset
            singledData.forEach((ag, j) => {
            if(j == 0) return;
            values.push(numberWithUnit(ag.data, d.number_unit));
            labels.push(ag.label)
            colors.push(colorScheme[j % colorScheme.length]);
            })
        } else if (aggregate) {
            disaggregatedData[i] = disaggregatedData[i] || {};
            disaggregatedData[i].label = d.name_item;
            disaggregatedData[i].color = d.background_color;

            ((data || {})[d.dataset_id] || []).forEach((ds) => {
            if (!disaggregations.includes(ds.label)) {
                disaggregations.push(ds.label);
            }

            // Save dataset values based on disaggregation
            // This is not an array yet, because we don't know the order of disaggregations.
            disaggregatedData[i][ds.label] = ds.data;
            });
        } else {
            ((data || {})[d.dataset_id] || []).sort((a, b) => a.label && b.label && a.label.toString().localeCompare(b.label.toString())).forEach((ds, j) => {
            const idx = _datasets.findIndex((d) => d.label == ds.label);
            let data = (_datasets[idx] || {}).data || [];
            data[i] = ds.data;

            const dataset = {
                data,
                label: ds.label,
                backgroundColor: colorSet ? colorSet[ds.label] : colorScheme[j % colorScheme.length],
            };

            if (idx == -1) {
                _datasets.push(dataset);
            } else {
                _datasets[idx] = dataset;
            }
            });

            labels.push(d.name_item);
        }
        });

        if (aggregate) {
        disaggregations.forEach((d) => {
            labels.push(d);

            datasets.forEach((ds, i) => {
            disaggregatedData[i].data = disaggregatedData[i].data || [];
            disaggregatedData[i].data.push(disaggregatedData[i][d]);
            });
        });

        Object.keys(disaggregatedData).forEach((k, i) => {
            const { label, data, color } = disaggregatedData[k];

            _datasets.push({
            label,
            data,
            backgroundColor: color || colorScheme[i % colorScheme.length],
            });
        });
        }

        return {
        labels,
        notConfigured,
        datasets:
            columns == "single"
            ? [
                {
                    label: "-",
                    data: values,
                    backgroundColor: colors,
                },
                ]
            : _datasets,
        };
    };

    return (
        <ChartContent
            chart_type='bar'
            {...{formatData, getChartOptions, getChartPlugins, data, widgetId, widgetRef}} 
        />
    )
}

export default BarChartDatasetWidget;
